import React from "react";
import { lightTheme } from "styles/theme";
import { Text } from "styles/utility-components";

import TooltipComponent from "components/ui/tooltip/Tooltip";
import { StageSelectTagContainer } from "lib/core_components/NoticeStage";
import { FrameworkStages } from "lib/types/graphQLEnums";
import { TenderStageV2Groups } from "lib/types/models";

type FrameworkStageProps = {
  stage?: FrameworkStages;
  onClick?: () => void;
};

type StageConfig = {
  id: FrameworkStages;
  label: string;
  group: TenderStageV2Groups;
  color: string;
  tooltip: string;
};

export const FRAMEWORK_STAGE_CONFIG: Record<FrameworkStages, StageConfig> = {
  [FrameworkStages.Upcoming]: {
    id: FrameworkStages.Upcoming,
    label: "Pre-tender",
    group: TenderStageV2Groups.PRE_TENDER,
    color: lightTheme.colors.sysProcStagePreTender,
    tooltip: "This framework is not yet open for suppliers to apply",
  },
  [FrameworkStages.Stale]: {
    id: FrameworkStages.Stale,
    label: "Stale Pre-tender",
    group: TenderStageV2Groups.PRE_TENDER,
    color: lightTheme.colors.sysProcStageStalePreTender,
    tooltip:
      "This framework is not yet open for suppliers to apply and the pre-tender notice is at least 3 months old",
  },
  [FrameworkStages.Tendering]: {
    id: FrameworkStages.Tendering,
    label: "Open",
    group: TenderStageV2Groups.TENDER,
    color: lightTheme.colors.sysProcStageOpenTender,
    tooltip: "This framework is open for suppliers to apply",
  },
  [FrameworkStages.Closed]: {
    id: FrameworkStages.Closed,
    label: "Closed",
    group: TenderStageV2Groups.TENDER,
    color: lightTheme.colors.sysProcStageClosedTender,
    tooltip: "This framework is closed for suppliers to apply, but has not yet been awarded",
  },
  [FrameworkStages.Awarded]: {
    id: FrameworkStages.Awarded,
    label: "Awarded",
    group: TenderStageV2Groups.AWARD,
    color: lightTheme.colors.sysProcStageAwardedContract,
    tooltip:
      "Suppliers have been awarded to this framework, but the contracts within have not started yet",
  },
  [FrameworkStages.Live]: {
    id: FrameworkStages.Live,
    label: "Live",
    group: TenderStageV2Groups.AWARD,
    color: lightTheme.colors.sysProcStageLive,
    tooltip: "Suppliers have been awarded to this framework and the contracts within have started",
  },
  [FrameworkStages.Expired]: {
    id: FrameworkStages.Expired,
    label: "Expired",
    group: TenderStageV2Groups.AWARD,
    color: lightTheme.colors.sysProcStageExpiredContract,
    tooltip: "Suppliers are no longer being awareded contracts through this framework",
  },
  [FrameworkStages.Cancelled]: {
    id: FrameworkStages.Cancelled,
    label: "Cancelled",
    group: TenderStageV2Groups.AWARD,
    color: lightTheme.colors.sysProcStageCancelledAward,
    tooltip: "This framework has been cancelled",
  },
};

export default function FrameworkStage({
  stage,
  ...props
}: FrameworkStageProps): JSX.Element | null {
  if (!stage) return null;
  const stageDetails = FRAMEWORK_STAGE_CONFIG[stage];

  return (
    <TooltipComponent content={<Text white>{stageDetails.tooltip}</Text>}>
      <StageSelectTagContainer color={stageDetails.color} {...props}>
        {stageDetails.label}
      </StageSelectTagContainer>
    </TooltipComponent>
  );
}
